/**
 * React Query configuration constants
 * Implements tiered caching strategy based on data mutability
 * 
 * Compliance: Performance optimization aligned with OWASP (no security impact)
 */

export const QUERY_STALE_TIME = {
  /** 30 minutes - For truly static data (audit events, templates) */
  STATIC: 1000 * 60 * 30,
  
  /** 5 minutes - For semi-static data (form invitations, org settings) */
  SEMI_STATIC: 1000 * 60 * 5,
  
  /** 30 seconds - For dynamic data (submissions, live updates) */
  DYNAMIC: 1000 * 30,
  
  /** No stale time - For critical real-time user data */
  REALTIME: 0,
} as const;

export const QUERY_GC_TIME = {
  /** 1 hour - Keep in memory for frequently accessed data */
  LONG: 1000 * 60 * 60,
  
  /** 10 minutes - Medium retention for semi-frequent access */
  MEDIUM: 1000 * 60 * 10,
  
  /** 5 minutes - Short retention for rarely revisited data */
  SHORT: 1000 * 60 * 5,
} as const;

/**
 * Default query options for consistent behavior across the app
 */
export const DEFAULT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: 1,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
} as const;

/**
 * List query defaults with pagination support
 */
export const LIST_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: QUERY_STALE_TIME.DYNAMIC,
  gcTime: QUERY_GC_TIME.MEDIUM,
} as const;

/**
 * Static data query options (templates, config)
 */
export const STATIC_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: QUERY_STALE_TIME.STATIC,
  gcTime: QUERY_GC_TIME.LONG,
} as const;

/**
 * Heavy org dashboard query options - longer stale time for expensive queries
 * with enhanced retry strategy for large data fetches
 */
export const ORG_DASHBOARD_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: QUERY_STALE_TIME.SEMI_STATIC, // 5 minutes for heavy dashboard data
  gcTime: QUERY_GC_TIME.LONG,
  retry: 2, // More retries for dashboard queries
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 15000),
  // Select optimization: prefer minimal fields for list queries
  // Individual components can override with full selects as needed
} as const;

/**
 * Org table list query options - optimized for table views
 * Uses shorter stale time since lists are frequently updated
 */
export const ORG_LIST_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: QUERY_STALE_TIME.DYNAMIC, // 30 seconds for active lists
  gcTime: QUERY_GC_TIME.MEDIUM,
  retry: 1,
} as const;

/**
 * Real-time critical data query options (user session, permissions)
 */
export const REALTIME_QUERY_OPTIONS = {
  refetchOnWindowFocus: true,
  refetchOnMount: true,
  retry: 2,
  staleTime: QUERY_STALE_TIME.REALTIME,
  gcTime: QUERY_GC_TIME.SHORT,
} as const;

/**
 * PDF and large file query options - for heavy operations like PDF processing
 */
export const HEAVY_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: QUERY_STALE_TIME.SEMI_STATIC,
  gcTime: QUERY_GC_TIME.MEDIUM,
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(2000 * 2 ** attemptIndex, 30000),
} as const;
